import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, X, Check, FileEdit, ChevronUp, ChevronDown, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { getErrorMessage } from '@/lib/errors';
import type { CampoTipo, OpcionCampo, CampoExploracionDefinicion } from '@/components/formularios-dinamicos/DynamicFormRenderer';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PlantillaExploracion {
  id: string;
  nombre: string;
  esquemaJson: CampoExploracionDefinicion[];
  isActive: boolean;
  createdAt: string;
}

interface CampoDraft {
  key: string;
  name: string;
  type: CampoTipo;
  label: string;
  required: boolean;
  options: OpcionCampo[];
  min: string;
  max: string;
}

const TIPO_LABEL: Record<CampoTipo, string> = {
  text: 'Texto',
  number: 'Número',
  select: 'Selección',
  checkbox: 'Casilla',
};

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
let campoKeySeq = 0;
function nextKey() { campoKeySeq += 1; return `campo-${Date.now()}-${campoKeySeq}`; }

function emptyCampo(): CampoDraft {
  return { key: nextKey(), name: '', type: 'text', label: '', required: false, options: [], min: '', max: '' };
}

function definicionToCampoDraft(c: CampoExploracionDefinicion): CampoDraft {
  return {
    key: nextKey(),
    name: c.name,
    type: c.type,
    label: c.label,
    required: !!c.required,
    options: c.options ?? [],
    min: c.min !== undefined ? String(c.min) : '',
    max: c.max !== undefined ? String(c.max) : '',
  };
}

function campoDraftToDefinicion(c: CampoDraft): CampoExploracionDefinicion {
  const def: CampoExploracionDefinicion = { name: c.name.trim(), type: c.type, label: c.label.trim() };
  if (c.required) def.required = true;
  if (c.type === 'select') def.options = c.options.map((o) => ({ value: o.value.trim(), label: o.label.trim() }));
  if ((c.type === 'text' || c.type === 'number') && c.min.trim() !== '') def.min = Number(c.min);
  if ((c.type === 'text' || c.type === 'number') && c.max.trim() !== '') def.max = Number(c.max);
  return def;
}

const NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

function validarCampos(campos: CampoDraft[]): string | null {
  if (campos.length === 0) return 'Agrega al menos un campo al esquema';
  const nombres = new Set<string>();
  for (const c of campos) {
    if (!c.name.trim()) return 'Todos los campos requieren un identificador técnico (name)';
    if (!NAME_REGEX.test(c.name.trim())) return `"${c.name}" no es un identificador válido (usa letras, números y guion bajo, sin iniciar con número)`;
    if (nombres.has(c.name.trim())) return `El identificador "${c.name}" está repetido`;
    nombres.add(c.name.trim());
    if (!c.label.trim()) return `El campo "${c.name}" necesita una etiqueta visible (label)`;
    if (c.type === 'select') {
      if (c.options.length === 0) return `El campo "${c.name}" es de tipo Selección y necesita al menos una opción`;
      const valores = new Set<string>();
      for (const o of c.options) {
        if (!o.value.trim() || !o.label.trim()) return `Completa el valor y la etiqueta de todas las opciones de "${c.name}"`;
        if (valores.has(o.value.trim())) return `El campo "${c.name}" tiene opciones con el mismo valor repetido`;
        valores.add(o.value.trim());
      }
    }
    if (c.min.trim() && Number.isNaN(Number(c.min))) return `El mínimo de "${c.name}" no es un número válido`;
    if (c.max.trim() && Number.isNaN(Number(c.max))) return `El máximo de "${c.name}" no es un número válido`;
    if (c.min.trim() && c.max.trim() && Number(c.min) > Number(c.max)) return `En "${c.name}" el mínimo no puede ser mayor al máximo`;
  }
  return null;
}

// ─── Toggle ──────────────────────────────────────────────────────────────────

function Toggle({ enabled, onChange, disabled }: { enabled: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'w-9 h-5 rounded-full relative transition-colors focus:outline-none flex-shrink-0',
        enabled ? 'bg-primary-500' : 'bg-gray-200',
        disabled && 'cursor-default opacity-60',
      )}
    >
      <span className={cn('absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform', enabled ? 'translate-x-4' : 'translate-x-0')} />
    </button>
  );
}

// ─── Field card ──────────────────────────────────────────────────────────────

function CampoCard({ campo, index, total, onChange, onRemove, onMove }: {
  campo: CampoDraft;
  index: number;
  total: number;
  onChange: (patch: Partial<CampoDraft>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
}) {
  function addOpcion() {
    onChange({ options: [...campo.options, { value: '', label: '' }] });
  }
  function updateOpcion(idx: number, patch: Partial<OpcionCampo>) {
    onChange({ options: campo.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)) });
  }
  function removeOpcion(idx: number) {
    onChange({ options: campo.options.filter((_, i) => i !== idx) });
  }

  return (
    <div className="border border-gray-200 rounded-xl p-3 space-y-3 bg-gray-50/50">
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-0.5 pt-1 flex-shrink-0">
          <button type="button" disabled={index === 0} onClick={() => onMove('up')}
            className="p-0.5 rounded hover:bg-gray-200 text-text-secondary disabled:opacity-30 disabled:hover:bg-transparent">
            <ChevronUp size={13} />
          </button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove('down')}
            className="p-0.5 rounded hover:bg-gray-200 text-text-secondary disabled:opacity-30 disabled:hover:bg-transparent">
            <ChevronDown size={13} />
          </button>
        </div>

        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] font-medium text-text-secondary mb-1">Identificador técnico (name)</label>
            <input value={campo.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="ej. presionIntraocular" className={inputCls} />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-text-secondary mb-1">Etiqueta visible (label)</label>
            <input value={campo.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="ej. Presión intraocular" className={inputCls} />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-text-secondary mb-1">Tipo de campo</label>
            <select value={campo.type} onChange={(e) => onChange({ type: e.target.value as CampoTipo })} className={inputCls}>
              {(Object.keys(TIPO_LABEL) as CampoTipo[]).map((t) => (
                <option key={t} value={t}>{TIPO_LABEL[t]}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 pt-4">
            <Toggle enabled={campo.required} onChange={() => onChange({ required: !campo.required })} />
            <span className="text-xs text-text-primary">Campo requerido</span>
          </div>

          {(campo.type === 'text' || campo.type === 'number') && (
            <>
              <div>
                <label className="block text-[10px] font-medium text-text-secondary mb-1">
                  {campo.type === 'number' ? 'Valor mínimo' : 'Longitud mínima'} (opcional)
                </label>
                <input type="number" value={campo.min} onChange={(e) => onChange({ min: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-text-secondary mb-1">
                  {campo.type === 'number' ? 'Valor máximo' : 'Longitud máxima'} (opcional)
                </label>
                <input type="number" value={campo.max} onChange={(e) => onChange({ max: e.target.value })} className={inputCls} />
              </div>
            </>
          )}
        </div>

        <button type="button" onClick={onRemove} className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors flex-shrink-0">
          <Trash2 size={13} />
        </button>
      </div>

      {campo.type === 'select' && (
        <div className="pl-6 space-y-2">
          <p className="text-[10px] font-medium text-text-secondary">Opciones</p>
          {campo.options.map((op, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input value={op.value} onChange={(e) => updateOpcion(idx, { value: e.target.value })} placeholder="valor" className={cn(inputCls, 'flex-1')} />
              <input value={op.label} onChange={(e) => updateOpcion(idx, { label: e.target.value })} placeholder="etiqueta visible" className={cn(inputCls, 'flex-1')} />
              <button type="button" onClick={() => removeOpcion(idx)} className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors flex-shrink-0">
                <X size={13} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addOpcion} className="flex items-center gap-1 text-[11px] text-primary-600 hover:text-primary-700">
            <Plus size={11} /> Agregar opción
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Builder modal ───────────────────────────────────────────────────────────

function PlantillaExploracionFormModal({ plantilla, onClose, onSaved }: {
  plantilla: PlantillaExploracion | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nombre, setNombre] = useState(plantilla?.nombre || '');
  const [campos, setCampos] = useState<CampoDraft[]>(() => (plantilla ? plantilla.esquemaJson.map(definicionToCampoDraft) : []));

  const saveMutation = useMutation({
    mutationFn: (dto: { nombre: string; esquemaJson: CampoExploracionDefinicion[] }) =>
      plantilla
        ? api.patch(`/api/formularios-dinamicos/plantillas/${plantilla.id}`, dto).then((r) => r.data)
        : api.post('/api/formularios-dinamicos/plantillas', dto).then((r) => r.data),
    onSuccess: () => {
      toast.success(plantilla ? 'Plantilla actualizada' : 'Plantilla creada');
      onSaved();
      onClose();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar la plantilla')),
  });

  function addCampo() {
    setCampos((cs) => [...cs, emptyCampo()]);
  }
  function updateCampo(key: string, patch: Partial<CampoDraft>) {
    setCampos((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  }
  function removeCampo(key: string) {
    setCampos((cs) => cs.filter((c) => c.key !== key));
  }
  function moveCampo(key: string, direction: 'up' | 'down') {
    setCampos((cs) => {
      const idx = cs.findIndex((c) => c.key === key);
      const swapWith = direction === 'up' ? idx - 1 : idx + 1;
      if (idx < 0 || swapWith < 0 || swapWith >= cs.length) return cs;
      const next = [...cs];
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) { toast.error('El nombre de la plantilla es requerido'); return; }
    const error = validarCampos(campos);
    if (error) { toast.error(error); return; }
    saveMutation.mutate({ nombre: nombre.trim(), esquemaJson: campos.map(campoDraftToDefinicion) });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-text-primary text-sm">
            {plantilla ? 'Editar plantilla de exploración' : 'Nueva plantilla de exploración'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Nombre de la plantilla *</label>
            <input className={inputCls} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej. Exploración Oftalmológica..." />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-text-secondary">Campos del formulario *</label>
              <button type="button" onClick={addCampo} className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
                <Plus size={13} /> Agregar campo
              </button>
            </div>

            {campos.length === 0 && (
              <div className="text-center py-8 border border-dashed border-gray-200 rounded-xl">
                <p className="text-xs text-text-secondary">Sin campos todavía. Agrega el primero.</p>
              </div>
            )}

            <div className="space-y-2">
              {campos.map((c, idx) => (
                <CampoCard
                  key={c.key}
                  campo={c}
                  index={idx}
                  total={campos.length}
                  onChange={(patch) => updateCampo(c.key, patch)}
                  onRemove={() => removeCampo(c.key)}
                  onMove={(dir) => moveCampo(c.key, dir)}
                />
              ))}
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose} className="text-sm border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 text-text-secondary transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 text-sm bg-primary-600 text-white px-5 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors font-medium"
          >
            <Check size={14} />
            {saveMutation.isPending ? 'Guardando...' : 'Guardar plantilla'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function PlantillasExploracionPage() {
  const { usuario } = useAuthStore();
  const qc = useQueryClient();
  const canWrite = usuario?.rolId === ROL.SUPER_ADMIN || usuario?.rolId === ROL.ADMIN_CLINICA || usuario?.rolId === ROL.MEDICO_ADMIN;

  const [editando, setEditando] = useState<PlantillaExploracion | null | 'new'>(null);

  const { data: plantillas = [], isLoading } = useQuery<PlantillaExploracion[]>({
    queryKey: ['plantillas-exploracion'],
    queryFn: () => api.get('/api/formularios-dinamicos/plantillas').then((r) => r.data),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/api/formularios-dinamicos/plantillas/${id}`, { isActive }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plantillas-exploracion'] });
      toast.success('Plantilla actualizada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar la plantilla')),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <FileEdit size={20} className="text-primary-600" />
            Formularios clínicos (Exploraciones dinámicas)
          </h2>
          <p className="text-sm text-text-secondary mt-0.5">
            Define campos de exploración física personalizados por especialidad, sin tocar la base de datos.
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => setEditando('new')}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <Plus size={14} /> Nueva plantilla
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>
        ) : plantillas.length === 0 ? (
          <div className="text-center py-16">
            <Layers size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">Sin plantillas de exploración</p>
            {canWrite && (
              <button onClick={() => setEditando('new')} className="mt-3 text-xs text-primary-600 hover:underline">
                Crear primera plantilla
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-text-secondary text-xs">
                  <th className="text-left px-4 py-2.5 font-medium">Nombre</th>
                  <th className="text-left px-4 py-2.5 font-medium">Campos</th>
                  <th className="text-left px-4 py-2.5 font-medium">Estado</th>
                  {canWrite && <th className="text-right px-4 py-2.5 font-medium">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {plantillas.map((p) => (
                  <tr key={p.id} className={cn('border-t border-gray-50 hover:bg-gray-50/50 transition-colors', !p.isActive && 'opacity-60')}>
                    <td className="px-4 py-3 font-medium text-text-primary">{p.nombre}</td>
                    <td className="px-4 py-3 text-text-secondary">{p.esquemaJson?.length ?? 0} campo{(p.esquemaJson?.length ?? 0) !== 1 ? 's' : ''}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'text-[10px] font-bold px-2 py-0.5 rounded-full border',
                        p.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200',
                      )}>
                        {p.isActive ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditando(p)} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary hover:text-text-primary transition-colors" title="Editar">
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => toggleMutation.mutate({ id: p.id, isActive: !p.isActive })}
                            title={p.isActive ? 'Desactivar' : 'Activar'}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editando !== null && (
        <PlantillaExploracionFormModal
          plantilla={editando === 'new' ? null : editando}
          onClose={() => setEditando(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['plantillas-exploracion'] })}
        />
      )}
    </div>
  );
}
